import React from "react";
import { Facebook, Linkedin, Instagram } from "lucide-react";

// DEF Logo
const DEF_LOGO = "https://customer-assets.emergentagent.com/job_clarity-steps/artifacts/5k4ib0ip_DEF%20.webp";

const FooterLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-muted-foreground hover:text-primary transition-colors text-sm"
  >
    {children}
  </a>
);

const Footer = () => {
  return (
    <footer className="bg-card border-t border-border print:hidden">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Logo & Mission */}
          <div className="lg:col-span-1">
            <a 
              href="https://www.decisioneducation.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block mb-4"
            >
              <img 
                src={DEF_LOGO} 
                alt="Decision Education Foundation" 
                className="h-12 w-auto"
              />
            </a>
            <p className="text-sm text-primary font-medium mb-3">
              Better Decisions ~ Better Lives
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              DEF improves the lives of young people by empowering them with effective Decision Skills.
            </p>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Sponsored by DEF:</p>
              <FooterLink href="https://www.societyofdecisioneducators.org/">
                Society of Decision Educators
              </FooterLink>
              <br />
              <FooterLink href="https://collegechoice.decisionacademy.org/">
                Decision Academy
              </FooterLink>
            </div>
          </div>

          {/* Resources For */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Resources For</h3>
            <ul className="space-y-2">
              <li>
                <FooterLink href="https://www.decisioneducation.org/explore/teacher">
                  Teachers
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/explore/counselor">
                  Counselors
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/explore/school-administrator">
                  Administrators
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/explore/district">
                  Districts
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/explore/parent">
                  Parents
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/explore/student">
                  Students
                </FooterLink>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <FooterLink href="https://www.decisioneducation.org/terms-of-service">
                  Terms of Service
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/privacy-policy">
                  Privacy Policy
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/cookie-policy">
                  Cookie Policy
                </FooterLink>
              </li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Connect</h3>
            <ul className="space-y-2">
              <li>
                <FooterLink href="https://www.decisioneducation.org/donate">
                  Donate
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/contact-us">
                  Contact Us
                </FooterLink>
              </li>
              <li>
                <FooterLink href="https://www.decisioneducation.org/mailing-list">
                  Mailing List
                </FooterLink>
              </li>
              <li>
                <a
                  href="tel:+16508149616"
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  +1 (650) 814 9616
                </a>
              </li>
            </ul>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">
                501(c)(3) Nonprofit
              </p>
              <p className="text-xs text-muted-foreground">
                EIN 77-0575573
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Copyright © {new Date().getFullYear()} Decision Education Foundation
          </p>
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.facebook.com/DecisionEducation/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="https://www.linkedin.com/company/decision-education-foundation/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a
              href="https://www.pinterest.com/DEFoundation/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors"
              aria-label="Pinterest"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
